import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
// import ChatBot from '../components/ChatBot';
import { APP_VERSION } from '../version';
import bannerService from '../services/banner.service';
import footerService from '../services/footer.service';
import quickMenuService from '../services/quickMenu.service';
import { useLanguage } from '../context/LanguageContext';
import sanitizeHTML from '../utils/sanitizer';
import '../styles/site.css';

function SiteLayout() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bannerImage, setBannerImage] = useState('');
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState('');
  const [footerTwo, setFooterTwo] = useState(null);
  const [footerThree, setFooterThree] = useState(null);
  const [footerFour, setFooterFour] = useState(null);
  const [internalLinks, setInternalLinks] = useState(null);
  const [importantLinks, setImportantLinks] = useState(null);
  const [legalLinks, setLegalLinks] = useState([]);
  const [quickMenuDocs, setQuickMenuDocs] = useState([]);
  const location = useLocation();
  const { currentLanguage, changeLanguage, t } = useLanguage();

  const languages = [
    { name: 'English', code: 'en', flag: '/assets/icons/gb.png' },
    { name: 'Swahili', code: 'sw', flag: '/assets/icons/tz.png' },
    { name: 'Chinese', code: 'zh', flag: '/assets/icons/cn.png' },
    { name: 'French', code: 'fr', flag: '/assets/icons/fr.png' },
    { name: 'Hindi', code: 'hi', flag: '/assets/icons/in.png' },
    { name: 'Arabic', code: 'ar', flag: '/assets/icons/sa.png' },
    { name: 'German', code: 'de', flag: '/assets/icons/de.png' },
    { name: 'Italian', code: 'it', flag: '/assets/icons/it.png' },
    { name: 'Russian', code: 'ru', flag: '/assets/icons/ru.png' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownOpen && !event.target.closest('.language-selector')) {
        setLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [languageDropdownOpen]);

  useEffect(() => {
    const fetchLayoutData = async () => {
      const [footerResult, legalResult, bannerResult, quickMenuResult] = await Promise.allSettled([
        footerService.getAllFooterSections(),
        footerService.getLegalFooterLinks(),
        bannerService.getBannerImage(),
        quickMenuService.getQuickMenuDocuments(54),
      ]);

      // Process footer sections
      const footerData = footerResult.status === 'fulfilled' ? footerResult.value : {};
      if (footerData.footerTwo) setFooterTwo(footerData.footerTwo);
      if (footerData.footerThree) setFooterThree(footerData.footerThree);
      if (footerData.footerFour) setFooterFour(footerData.footerFour);
      if (footerData.internalLinks) setInternalLinks(footerData.internalLinks);
      if (footerData.importantLinks) setImportantLinks(footerData.importantLinks);

      // Process legal links
      if (legalResult.status === 'fulfilled' && legalResult.value?.success && legalResult.value?.data) {
        setLegalLinks(legalResult.value.data);
      }

      // Process banner
      if (bannerResult.status === 'fulfilled' && bannerResult.value?.imageUrl) {
        setBannerImage(bannerResult.value.imageUrl);
      }

      // Process quick menu documents
      if (quickMenuResult.status === 'fulfilled' && quickMenuResult.value?.success && quickMenuResult.value?.data) {
        setQuickMenuDocs(quickMenuResult.value.data);
      }
    };

    fetchLayoutData();
  }, []);

  // Add target="_blank" to all links in footer sections rendered from API HTML
  useEffect(() => {
    const footerLinks = document.querySelectorAll('.site-footer .footer-column a');
    footerLinks.forEach(link => {
      if (!link.hasAttribute('target')) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });
  }, [internalLinks, footerTwo, footerThree, importantLinks]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLanguageChange = (language) => {
    changeLanguage(language.code);
    setLanguageDropdownOpen(false);
  };

  const toggleLanguageDropdown = () => {
    setLanguageDropdownOpen(!languageDropdownOpen);
  };

  const sanitizeSearchInput = (value) => value.replace(/[^a-zA-Z0-9 ]/g, '');

  const handleSearchInputChange = (e) => {
    const raw = e.target.value;
    const sanitized = sanitizeSearchInput(raw);
    if (raw !== sanitized) {
      setSearchError('Search keywords can only contain letters, numbers, and spaces. Special characters are not allowed.');
    } else {
      setSearchError('');
    }
    setSearchQuery(sanitized);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    const invalidRegex = /[^a-zA-Z0-9 ]/;
    if (invalidRegex.test(trimmed)) {
      setSearchError('Search keywords can only contain letters, numbers, and spaces. Special characters are not allowed.');
      return;
    }
    setSearchError('');
    sessionStorage.setItem('searchQuery', trimmed);
    setSearchQuery('');
    navigate('/search');
  };

  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === currentLanguage) || languages[1];
  };

  return (
    <div className="site-wrapper">

      {/* Top Quick Menu Bar */}
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-content">
            <div className="quick-menu">
              <span className="quick-menu-label">{t('quickMenu.label')}</span>
              {quickMenuDocs.map((doc) => (
                <a
                  key={doc.infoId}
                  href={doc.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {doc.title}
                </a>
              ))}
              <a href="/faq">{t('quickMenu.faqs')}</a>
              <a href="/public-notice">{t('quickMenu.hotNews')}</a>
            </div>

            <form className="top-bar-search" onSubmit={handleSearch}>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  className={`search-topBar-input${searchError ? ' error' : ''}`}
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  aria-label="Search website"
                />
                {searchError && <span className="search-validation-tooltip">{searchError}</span>}
              </div>
              <button type="submit" className="search-button" aria-label="Submit search">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </form>

            <div className="language-selector">
              <div className="language-current" onClick={toggleLanguageDropdown}>
                <img
                  src={getCurrentLanguage().flag}
                  alt={getCurrentLanguage().name}
                  className="language-flag"
                />
                <span>{getCurrentLanguage().name} ▼</span>
              </div>
              {languageDropdownOpen && (
                <div className="language-dropdown">
                  {languages.map((language) => (
                    <div
                      key={language.code}
                      className={`language-option ${currentLanguage === language.code ? 'active' : ''}`}
                      onClick={() => handleLanguageChange(language)}
                    >
                      <img
                        src={language.flag}
                        alt={language.name}
                        className="language-flag"
                      />
                      <span>{language.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Header Banner */}
      {/* <header className="site-header">
        <img className="header-banner" src={bannerImage} alt="Immigration Badge" />
      </header> */}

      <header className="site-header">
        <div className="header-banner-content">
          <img src="/assets/images/taifa.png" alt="Taifa Logo" className="header-logo header-logo-left" />
          <div className="header-text">
            <h1>The United Republic of Tanzania</h1>
            <h1>Ministry of Home Affairs</h1>
            <h3>IMMIGRATION SERVICES DEPARTMENT</h3>
          </div>
          <img src="/assets/images/uhamiaji.png" alt="Immigration Logo" className="header-logo header-logo-right" />
        </div>
        <div className="tz-gov-line"></div>
      </header>

      {/* Main Navigation */}
      <nav className="main-nav">
        <div className="container">
          <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
            ☰
          </button>
          <ul className={`nav-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <li><NavLink to="/" onClick={() => setMobileMenuOpen(false)}>{t('nav.home')}</NavLink></li>
            <li><NavLink to="/about-us" onClick={() => setMobileMenuOpen(false)}>{t('nav.aboutUs')}</NavLink></li>
            <li><NavLink to="/immigration-services" onClick={() => setMobileMenuOpen(false)}>{t('nav.immigrationServices')}</NavLink></li>
            <li><NavLink to="/service-fee" onClick={() => setMobileMenuOpen(false)}>{t('nav.serviceFee')}</NavLink></li>
            <li><NavLink to="/public-notice" onClick={() => setMobileMenuOpen(false)}>{t('nav.publicNotice')}</NavLink></li>
            <li><NavLink to="/e-library" onClick={() => setMobileMenuOpen(false)}>{t('nav.eLibrary')}</NavLink></li>
            <li><NavLink to="/tourist-info" onClick={() => setMobileMenuOpen(false)}>{t('nav.eServices')}</NavLink></li>
            <li><NavLink to="/contact-us" onClick={() => setMobileMenuOpen(false)}>{t('nav.contactUs')}</NavLink></li>
          </ul>
        </div>
      </nav>

      {/* Page Content */}
      <main className="main-content">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="site-footer">
        <div className="container">
          <div className="footer-columns">
            {internalLinks && (
              <div className="footer-column">
                <h4>{internalLinks.Title}</h4>
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(internalLinks.Description).replace(
                      /<a(?=[\s>])(?![^>]*\btarget=)/gi,
                      '$& target="_blank" rel="noopener noreferrer"'
                    ),
                  }}
                />
              </div>
            )}

            {/* footer-two */}
            {footerTwo && (
              <div className="footer-column">
                <h4>{footerTwo.Title}</h4>
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(footerTwo.Description).replace(
                      /<a(?=[\s>])(?![^>]*\btarget=)/gi,
                      '$& target="_blank" rel="noopener noreferrer"'
                    ),
                  }}
                />
              </div>
            )}

            {/* footer-three */}
            {footerThree && (
              <div className="footer-column">
                <h4>{footerThree.Title}</h4>
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(footerThree.Description).replace(
                      /<a(?=[\s>])(?![^>]*\btarget=)/gi,
                      '$& target="_blank" rel="noopener noreferrer"'
                    ),
                  }}
                />
              </div>
            )}

            {/* footer-four */}
            {importantLinks && (
              <div className="footer-column">
                <h4>{importantLinks.Title}</h4>
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(importantLinks.Description).replace(
                      /<a(?=[\s>])(?![^>]*\btarget=)/gi,
                      '$& target="_blank" rel="noopener noreferrer"'
                    ),
                  }}
                />
              </div>
            )}
          </div>
          <div className="footer-copyright">
            <div className="footer-legal-links">
              {legalLinks.map((link) => (
                <a key={link.key} href={link.route} target="_blank" rel="noopener noreferrer">{link.label}</a>
              ))}
            </div>
            <p>{t('footer.copyright')} <span className="footer-version">
              {/* v{APP_VERSION} */}
              </span></p>
          </div>
        </div>
      </footer>

      {/* Fixed Social Media Sidebar */}
      <div className="social-media-sidebar">
        <a
          href="https://web.facebook.com/uhamiajitz"
          target="_blank"
          rel="noopener noreferrer"
          className="social-icon"
          aria-label="Facebook"
        >
          <img src="/assets/icons/facebook.png" alt="Facebook" />
        </a>
        <a
          href="https://www.instagram.com/uhamiajitz"
          target="_blank"
          rel="noopener noreferrer"
          className="social-icon"
          aria-label="Instagram"
        >
          <img src="/assets/icons/instagram.png" alt="Instagram" />
        </a>
        <a
          href="https://x.com/uhamiajitz?s=21"
          target="_blank"
          rel="noopener noreferrer"
          className="social-icon"
          aria-label="Twitter"
        >
          <img src="/assets/icons/twitter.png" alt="Twitter" />
        </a>
        <a
          href="https://www.tiktok.com/@uhamiaji.tz"
          target="_blank"
          rel="noopener noreferrer"
          className="social-icon"
          aria-label="TikTok"
        >
          <img src="/assets/icons/tiktok.png" alt="TikTok" />
        </a>
        <a
          href="https://www.youtube.com/@UhamiajiTz"
          target="_blank"
          rel="noopener noreferrer"
          className="social-icon"
          aria-label="YouTube"
        >
          <img src="/assets/icons/youtube.png" alt="YouTube" />
        </a>
      </div>
      {/* <ChatBot /> */}
    </div>
  );
}

export default SiteLayout;